import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Clock,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import TemplateMessage from "../../components/TemplateMessage";
import ModeloWhatsappModal from "../../components/ModeloWhatsappModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Modelos aprovados do WhatsApp oficial.
//
// Dois caminhos, e a tela oferece os dois: puxar da Meta o que já foi aprovado
// por fora, e criar um novo mandando para aprovação. Sem modelo aprovado, o
// WhatsApp oficial não deixa falar com quem não escreveu nas últimas 24 h — é
// o que trava campanha e retomada de conversa.

const SITUACOES = {
  APPROVED: {
    rotulo: "Aprovado",
    classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
  },
  PENDING: {
    rotulo: "Em análise na Meta",
    classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400"
  },
  REJECTED: {
    rotulo: "Reprovado",
    classe: "bg-destructive/10 text-destructive"
  },
  LOCAL: {
    rotulo: "Não enviado à Meta",
    classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
  },
  PAUSED: {
    rotulo: "Pausado pela Meta",
    classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
  },
  DISABLED: {
    rotulo: "Desativado",
    classe: "bg-muted text-muted-foreground"
  }
};

const situacaoDe = (status) =>
  SITUACOES[String(status || "").toUpperCase()] || {
    rotulo: status || "—",
    classe: "bg-muted text-muted-foreground"
  };

const ROTULO_CATEGORIA = {
  MARKETING: "Marketing",
  UTILITY: "Utilitário",
  AUTHENTICATION: "Autenticação"
};

// Do formato da Meta para o desenho da conversa.
const previaDe = (template) => {
  const componentes = template.components || [];

  const acharTipo = (tipo) =>
    componentes.find((c) => String(c?.type).toUpperCase() === tipo);

  const cabecalho = acharTipo("HEADER");
  const botoes = acharTipo("BUTTONS");

  return {
    tipo: "whatsapp",
    nome: template.nome,
    categoria: template.categoria,
    cabecalho: cabecalho
      ? {
          tipo: String(cabecalho.format || "TEXT").toLowerCase(),
          texto: cabecalho.text
        }
      : null,
    corpo: acharTipo("BODY")?.text,
    rodape: acharTipo("FOOTER")?.text || null,
    botoes: (botoes?.buttons || []).map((b) => ({
      tipo: String(b.type || "").toLowerCase(),
      texto: b.text,
      url: b.url,
      telefone: b.phone_number
    }))
  };
};

const ModelosWhatsapp = () => {
  const [templates, setTemplates] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      // Unificada: os da Cloud API da Meta e os do WhatsApp conectado na
      // Zernio. Ler só uma origem escondia metade dos modelos de quem usa as
      // duas — e quem escolhe um modelo não deveria precisar saber por onde
      // a conexão dele passa.
      const { data } = await api.get("/meta/templates/available");
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Puxar primeiro: quem já tem modelo aprovado na Meta não deveria precisar
  // recriá-lo aqui e esperar uma segunda aprovação.
  const sincronizar = async () => {
    setSincronizando(true);

    try {
      // Sincroniza a Meta (que guarda cópia local) e recarrega — o recarregar
      // relê a Zernio ao vivo, que não tem cópia por aqui.
      const { data } = await api.post("/meta/templates/sync").catch(() => ({
        data: { total: 0 }
      }));

      await carregar();

      toast.success(
        data?.total
          ? `${data.total} modelos trazidos da Meta.`
          : "Lista atualizada. Se não apareceu nada, ainda não há modelo aprovado nas suas conexões."
      );
    } catch (err) {
      toastError(err);
    }

    setSincronizando(false);
  };

  const apagar = async (template) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Apagar "${template.nome}"? Ele sai daqui e da Meta, e campanhas que o usam param de enviar.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/meta/templates/${template.id}`);
      toast.success("Modelo apagado.");
      await carregar();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Modelos do WhatsApp
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Mensagens aprovadas pela Meta, vindas da Cloud API ou do WhatsApp
            conectado na Zernio. São o único jeito de falar com quem não
            escreveu nas últimas 24 h — é o que campanha e retomada de conversa
            usam.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={sincronizar}
            disabled={sincronizando}
          >
            {sincronizando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Buscar modelos
          </Button>
          <Button onClick={() => setCriando(true)}>
            <Plus className="h-4 w-4" />
            Criar modelo
          </Button>
        </div>
      </div>

      {carregando ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <FileText className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-medium">Nenhum modelo ainda</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Se você já tem modelos aprovados — na Cloud API da Meta ou no
            WhatsApp conectado na Zernio — use “Buscar modelos”. Se não tem,
            crie um aqui: ele vai para aprovação e costuma voltar em alguns
            minutos.
          </p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={sincronizar}>
              <RefreshCw className="h-4 w-4" />
              Buscar modelos
            </Button>
            <Button onClick={() => setCriando(true)}>
              <Plus className="h-4 w-4" />
              Criar modelo
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => {
            const situacao = situacaoDe(template.status);
            // Só o que veio da Cloud API da Meta é gerenciado aqui. O da
            // Zernio é mantido no painel dela — apagar daqui daria a impressão
            // falsa de que sumiu de lá.
            const daMeta = template.origem === "meta";

            return (
              <div key={template.chave} className="rounded-xl border bg-card p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-mono text-sm font-medium">
                    {template.nome}
                  </span>
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                      situacao.classe
                    )}
                  >
                    {situacao.rotulo}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-[10px]">
                    {ROTULO_CATEGORIA[template.categoria] || template.categoria}
                  </Badge>
                  {template.idioma && (
                    <span className="uppercase">{template.idioma}</span>
                  )}
                  {/* De onde veio: os dois caminhos existem lado a lado, e
                      saber qual é decide onde mexer no modelo. */}
                  <Badge variant="outline" className="text-[10px]">
                    {daMeta ? "Meta" : "Zernio"}
                  </Badge>
                </div>

                {/* O motivo da reprovação é o que permite corrigir; sem ele
                    sobra recriar às cegas. */}
                {template.rejectedReason && (
                  <p className="mt-2 inline-flex items-start gap-1 text-xs text-destructive">
                    <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" />
                    {template.rejectedReason}
                  </p>
                )}

                {String(template.status).toUpperCase() === "PENDING" && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Não dá para enviar enquanto a Meta não aprovar.
                  </p>
                )}

                <div className="mt-2">
                  <TemplateMessage dados={previaDe(template)} />
                </div>

                <div className="mt-2 flex items-center justify-end border-t pt-2">
                  {daMeta ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => apagar(template)}
                      aria-label="Apagar modelo"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Gerenciado no painel da Zernio
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ModeloWhatsappModal
        open={criando}
        onClose={() => setCriando(false)}
        onCriado={carregar}
      />
    </div>
  );
};

export default ModelosWhatsapp;
